import { dateTime } from '@/lib/format'
import { useMediaUrl } from '@/lib/useMediaUrl'

/**
 * Shows exactly what the customer sent, beside the parse.
 *
 * This panel is the whole point of the review screen: an operator must be
 * able to check the machine's reading against the original photo or voice
 * note without leaving the page or opening WhatsApp on their phone.
 */
export default function SourcePanel({ message }) {
  const hasMedia = message?.msg_type === 'image' || message?.msg_type === 'audio'
  const { url: mediaUrl, failed } = useMediaUrl(message?.id, hasMedia)

  if (!message) {
    return (
      <div className="source-panel">
        <div className="panel-title">Source</div>
        <p className="muted">Entered manually — no customer message.</p>
      </div>
    )
  }

  return (
    <div className="source-panel">
      <div className="panel-title">
        What the customer sent
        <span className="muted"> · {dateTime(message.received_at)}</span>
      </div>

      {failed && (
        <p className="missing">
          The original {message.msg_type} could not be loaded. It may not have
          been downloaded from WhatsApp — check the Messages page.
        </p>
      )}

      {message.msg_type === 'image' && mediaUrl && (
        <figure className="source-image">
          <img src={mediaUrl} alt="Handwritten order slip" />
          <figcaption>
            <a href={mediaUrl} target="_blank" rel="noreferrer">
              Open full size
            </a>
          </figcaption>
        </figure>
      )}

      {message.msg_type === 'audio' && mediaUrl && (
        <div className="source-audio">
          {/* Let the reviewer listen to the original, not just trust the
              transcript. 'metadata' rather than 'none' so the duration shows
              before playback — a player reading 0:00 looks broken. */}
          <audio controls src={mediaUrl} preload="metadata">
            Your browser cannot play this voice note.
          </audio>
        </div>
      )}

      {hasMedia && !mediaUrl && !failed && (
        <p className="muted">Loading the original…</p>
      )}

      {message.body && (
        <div className="source-block">
          <div className="source-label">Message text</div>
          <p className="source-text">{message.body}</p>
        </div>
      )}

      {message.transcript && (
        <div className="source-block">
          <div className="source-label">Transcript</div>
          <p className="source-text">{message.transcript}</p>
        </div>
      )}

      {message.ocr_text && (
        <div className="source-block">
          <div className="source-label">Read from the image</div>
          <pre className="source-text preformatted">{message.ocr_text}</pre>
        </div>
      )}
    </div>
  )
}
